import { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ReadingProgressBar from "./components/ReadingProgressBar";
import LoadingIndicator from './components/LoadingIndicator';
import CookieConsent from './components/CookieConsent';
import AnimatedBackground from './components/AnimatedBackground';
import {
  getAuth,
  browserLocalPersistence,
  onAuthStateChanged,
  User,
} from "firebase/auth";
import fetchUserRoles from "./utils/fetchUserRoles";
import { UserRoles } from "./utils/types";
import { getUserRole } from "./utils/roleHelpers";
import { smoothScrollToTop } from "./utils/smoothScrollToTop";
import "./styles/main.scss";

const HomePage = lazy(() => import("./pages/HomePage"));
const AddPostPage = lazy(() => import("./pages/AddPostPage"));
const EditPostPage = lazy(() => import("./pages/EditPostPage"));
const PostPage = lazy(() => import("./pages/PostPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const SavedPage = lazy(() => import("./pages/SavedPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));

function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<UserRoles>({});

  useEffect(() => {
    const auth = getAuth();
    auth.setPersistence(browserLocalPersistence);
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        const roles = await fetchUserRoles();
        setUserRoles(roles);

        // Initialize roles document if it doesn't exist, or add new user if not present
        if (currentUser) {
          const { doc, getDoc, setDoc } = await import("firebase/firestore");
          const { db } = await import("./firebase");
          const rolesDocRef = doc(db, "userRoles", "roles");
          const rolesDocSnapshot = await getDoc(rolesDocRef);

          if (!rolesDocSnapshot.exists()) {
            // Create the document with the current user as admin
            // This allows initial setup when the document doesn't exist
            await setDoc(rolesDocRef, {
              [currentUser.uid]: {
                role: "admin",
                email: currentUser.email || "",
                canComment: true,
              },
            });
            // Refresh roles after creation
            const updatedRoles = await fetchUserRoles();
            setUserRoles(updatedRoles);
          } else {
            // Document exists - check if current user is in it, if not add them with default "user" role
            const rolesData = rolesDocSnapshot.data();
            if (rolesData && !rolesData[currentUser.uid]) {
              // User is not in roles document, add them with default "user" role
              await setDoc(
                rolesDocRef,
                {
                  [currentUser.uid]: {
                    role: "user",
                    email: currentUser.email || "",
                    canComment: true,
                  },
                },
                { merge: true }
              );
              // Refresh roles after adding
              const updatedRoles = await fetchUserRoles();
              setUserRoles(updatedRoles);

              // Send welcome notification to new user (from site owner)
              try {
                const { collection, addDoc, serverTimestamp } = await import("firebase/firestore");
                const SITE_OWNER_UID = "BVZrd8lU9sdhRbQnCTHhrcD2mdm1";
                await addDoc(collection(db, "notifications"), {
                  recipientUid: currentUser.uid,
                  fromUid: SITE_OWNER_UID,
                  fromName: "Kraffcho",
                  type: "welcome",
                  read: false,
                  createdAt: serverTimestamp(),
                });
              } catch (notifError) {
                console.error("Error creating welcome notification:", notifError);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
        setUserRoles({}); // Set empty roles on error
      }

      if (currentUser) {
        setIsAuthenticated(true);
        setUser(currentUser);

        // Sync user profile to Firestore
        try {
          const { syncUserProfile } = await import("./utils/userProfile");
          await syncUserProfile(currentUser);
        } catch (error) {
          console.error("Error syncing user profile:", error);
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const canAddPosts = (): boolean => {
    const role = getUserRole(userRoles[user?.uid || ""]);
    return role === "admin" || role === "writer";
  };

  const isAdmin = (): boolean => {
    const role = getUserRole(userRoles[user?.uid || ""]);
    return role === "admin";
  };

  return { isAuthenticated, user, loading, userRoles, canAddPosts, isAdmin };
}

function AppRoutes(props: any) {
  const { isAuthenticated, user, userRoles, canAddPosts, isAdmin } = props;

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <Routes>
        <Route
          path="/"
          element={<HomePage user={user} userRoles={userRoles} />}
        />
        <Route
          path="/category/:categoryName"
          element={<HomePage user={user} userRoles={userRoles} />}
        />
        <Route
          path="/tag/:tagName"
          element={<HomePage user={user} userRoles={userRoles} />}
        />
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to="/" /> : <LoginPage />}
        />
        <Route
          path="/register"
          element={isAuthenticated ? <Navigate to="/" /> : <RegisterPage />}
        />
        <Route
          path="/add-post"
          element={
            isAuthenticated && canAddPosts() ? (
              <AddPostPage />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/edit-post/:id"
          element={
            isAuthenticated ? (
              <EditPostPage userRoles={userRoles} />
            ) : (
              <Navigate to="/login" />
            )
          }
        />
        <Route
          path="/post/:id"
          element={
            <>
              <ReadingProgressBar />
              <PostPage userRoles={userRoles} />
            </>
          }
        />
        <Route
          path="/saved"
          element={isAuthenticated ? <SavedPage /> : <Navigate to="/login" />}
        />
        <Route
          path="/projects"
          element={<ProjectsPage />}
        />
        <Route
          path="/about"
          element={<AboutPage />}
        />
        <Route
          path="/admin"
          element={
            isAuthenticated && isAdmin() ? (
              <AdminPage userRoles={userRoles} />
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route path="/sitemap" element={<SitemapPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route
          path="/profile/:uid"
          element={<UserProfilePage userRoles={userRoles} />}
        />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

function AppContent() {
  const location = useLocation();
  const auth = useAuth();

  // Scroll to top when the route changes (any navigation)
  useEffect(() => {
    smoothScrollToTop();
  }, [location.pathname]);

  // Hide animated background on 404 page
  // Check if current path matches any known route pattern
  const knownRoutes = [
    "/",
    "/login",
    "/register",
    "/add-post",
    "/projects",
    "/about",
    "/admin",
    "/saved",
    "/sitemap",
    "/privacy",
    "/terms",
    "/cookies",
  ];

  const isKnownRoute = knownRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/post/") ||
    location.pathname.startsWith("/edit-post/") ||
    location.pathname.startsWith("/category/") ||
    location.pathname.startsWith("/tag/") ||
    location.pathname.startsWith("/profile/");

  const isNotFoundPage = !isKnownRoute;

  if (auth.loading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {!isNotFoundPage && <AnimatedBackground />}
      <Navbar user={auth.user} userRoles={auth.userRoles} />
      <AppRoutes {...auth} />
      <Footer />
      <ScrollToTop />
      <CookieConsent />
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
